// Panel izquierdo: las capturas que mandó el cliente.
//
// Portado de WhatsAppThreadPanel.jsx: lista vertical, separadores de día
// (Hoy / Ayer / dd/mm/aaaa), hora corta al pie de cada burbuja, y el estado
// vacío en el mismo tono.
//
// Net-new frente al original: allí las fotos eran un marco con el texto "Foto
// adjunta" y las notas de voz un stub con la duración — no había ni imagen ni
// reproducción. Acá las miniaturas son URLs firmadas reales del bucket y el
// audio se reproduce de verdad.
//
// Las capturas son inmutables: no se editan ni se borran desde esta superficie.

import { useEffect, useState } from 'react';
import { FileText, Image as ImageIcon, Mic, Plus, X } from 'lucide-react';
import { supabase } from '../../supabaseClient';

const BUCKET_POR_TIPO = {
  image: 'desarrollos-images',
  factura: 'desarrollos-facturas',
  voice: 'desarrollos-voice',
};

const horaCorta = (ts) => {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const etiquetaDia = (ts) => {
  const d = new Date(ts);
  const hoy = new Date();
  const ayer = new Date(hoy);
  ayer.setDate(hoy.getDate() - 1);
  const mismo = (a, b) =>
    a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();
  if (mismo(d, hoy)) return 'Hoy';
  if (mismo(d, ayer)) return 'Ayer';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

// raw_payload_url viene como "bucket/ruta/archivo"; firmamos por una hora.
const firmar = async (rawPath, sourceType) => {
  if (!rawPath) return null;
  const bucketEsperado = BUCKET_POR_TIPO[sourceType];
  let bucket = bucketEsperado;
  let ruta = rawPath;
  const corte = rawPath.indexOf('/');
  if (corte > 0 && rawPath.slice(0, corte).startsWith('desarrollos-')) {
    bucket = rawPath.slice(0, corte);
    ruta = rawPath.slice(corte + 1);
  }
  if (!bucket) return null;
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(ruta, 3600);
  if (error) {
    console.error('[Desarrollos/capturas] no se pudo firmar', rawPath, error);
    return null;
  }
  return data?.signedUrl || null;
};

const EventoCaptura = ({ evento, urlFirmada, onPromover, onAbrirLightbox }) => {
  const conteoExtraidos = evento.extracted_fields?.items?.length || 0;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5">
      {evento.source_type === 'image' && (
        <>
          {urlFirmada ? (
            <button
              type="button"
              onClick={() => onAbrirLightbox(urlFirmada)}
              className="block w-full overflow-hidden rounded-md border border-slate-200"
            >
              <img
                src={urlFirmada}
                alt="Foto enviada por el cliente"
                className="block max-h-44 w-full object-cover"
              />
            </button>
          ) : (
            <div className="flex aspect-[4/3] w-full items-center justify-center rounded-md border border-slate-200 bg-[#f8fafc]">
              <ImageIcon size={20} className="text-slate-300" aria-hidden="true" />
            </div>
          )}
          <p className="mt-1.5 text-[11px] text-slate-500">Foto</p>
        </>
      )}

      {evento.source_type === 'factura' && (
        <a
          href={urlFirmada || undefined}
          target="_blank"
          rel="noreferrer"
          className={`flex items-center gap-2 rounded-md border border-slate-200 px-2 py-2 text-[11px] ${
            urlFirmada ? 'text-slate-700 hover:bg-slate-50' : 'pointer-events-none text-slate-400'
          }`}
        >
          <FileText size={14} aria-hidden="true" />
          Factura — abrir
        </a>
      )}

      {evento.source_type === 'voice' && (
        <>
          {urlFirmada ? (
            <audio controls src={urlFirmada} className="w-full" preload="none">
              Tu navegador no puede reproducir esta nota de voz.
            </audio>
          ) : (
            <span className="flex items-center gap-1.5 rounded-md border border-slate-200 px-2 py-1 text-[11px] text-slate-400">
              <Mic size={12} aria-hidden="true" /> Nota de voz
            </span>
          )}
          {evento.raw_transcript && (
            <p className="mt-1.5 whitespace-pre-wrap text-[11px] italic text-slate-600">
              “{evento.raw_transcript}”
            </p>
          )}
        </>
      )}

      {evento.source_type === 'manual' && (
        <div className="rounded-md border border-slate-200 bg-[#f8fafc] p-2">
          {Object.entries(evento.raw_manual_json || {}).length === 0 ? (
            <p className="text-[11px] text-slate-400">Entrada manual sin datos.</p>
          ) : (
            Object.entries(evento.raw_manual_json).map(([k, v]) => (
              <p key={k} className="text-[11px] text-slate-600">
                <span className="font-semibold text-slate-500">{k}:</span>{' '}
                {typeof v === 'object' ? JSON.stringify(v) : String(v)}
              </p>
            ))
          )}
        </div>
      )}

      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-[10px] text-slate-400">{horaCorta(evento.created_at)}</span>
        <button
          type="button"
          onClick={() => onPromover(evento)}
          className="inline-flex min-h-[32px] items-center gap-1 rounded-md border border-slate-200 px-2 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
        >
          <Plus size={11} aria-hidden="true" />
          {conteoExtraidos > 0
            ? `Promover ${conteoExtraidos} ${conteoExtraidos === 1 ? 'ítem' : 'ítems'}`
            : 'Promover a ítem'}
        </button>
      </div>
    </div>
  );
};

const IntakeFeedPanel = ({ eventos = [], onPromover }) => {
  const [urls, setUrls] = useState({});
  const [lightbox, setLightbox] = useState(null);

  // Firma perezosa: solo los eventos que traen archivo.
  useEffect(() => {
    let cancelado = false;
    (async () => {
      const pendientes = eventos.filter((e) => e.raw_payload_url && !urls[e.id]);
      if (pendientes.length === 0) return;
      const entradas = await Promise.all(
        pendientes.map(async (e) => [e.id, await firmar(e.raw_payload_url, e.source_type)])
      );
      if (cancelado) return;
      setUrls((prev) => ({ ...prev, ...Object.fromEntries(entradas) }));
    })();
    return () => {
      cancelado = true;
    };
  }, [eventos, urls]);

  // whatsapp nunca se dibuja acá aunque el constraint lo permita.
  const visibles = eventos.filter((e) => e.source_type !== 'whatsapp');
  let diaAnterior = null;

  return (
    <div className="flex h-full min-h-0 flex-col rounded-lg border border-slate-200 bg-[#f8fafc]">
      <div className="border-b border-slate-200 bg-white px-4 py-2.5">
        <h3 className="text-sm font-bold text-slate-900">Capturas del cliente</h3>
      </div>

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2.5">
        {visibles.length === 0 && (
          <p className="px-2 py-6 text-center text-xs text-slate-400">
            Aún no hay capturas para este Anexo. El cliente aún no ha enviado información.
          </p>
        )}

        {visibles.map((evento) => {
          const dia = etiquetaDia(evento.created_at);
          const mostrarDia = dia !== diaAnterior;
          diaAnterior = dia;
          return (
            <div key={evento.id}>
              {mostrarDia && (
                <p className="my-2 text-center text-[10px] font-medium uppercase tracking-wide text-slate-400">
                  {dia}
                </p>
              )}
              <EventoCaptura
                evento={evento}
                urlFirmada={urls[evento.id]}
                onPromover={onPromover}
                onAbrirLightbox={setLightbox}
              />
            </div>
          );
        })}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6"
          onClick={() => setLightbox(null)}
          role="presentation"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            aria-label="Cerrar"
            className="absolute right-5 top-5 text-white"
          >
            <X size={22} />
          </button>
          <img src={lightbox} alt="Captura ampliada" className="max-h-full max-w-full object-contain" />
        </div>
      )}
    </div>
  );
};

export default IntakeFeedPanel;
